import * as React from 'react';

import { LocalizedText } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';

import { SearchCriteriaModel } from 'src/common/stores/models';

import { UitkExpandoLink, UitkExpandoLinkTypeSize } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';

import { DateUtils } from 'utils/date-utils';

import { CarPickUpDropOffTimeSelect } from 'components/multi-item/search-wizard/car-pickup-dropoff-time-select';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { getBexApiDateTimeFromLocKey } from 'components/multi-item/bex-api-date-time-to-time-loc-key';
import { SearchFormFieldProps } from 'components/common/search-form';

interface CarPickUpDropOffProps extends LocalizedComponentProps, SearchFormFieldProps {
  model: SearchCriteriaModel;
  validators: any[];
  updateModelCallback: (event?: Event) => void;
}

interface CarPickUpDropOffState {
  invalidText: string;
}

@withLocalization
@FormFieldBinding
export class CarPickupDropOffTimeRange extends React.Component<CarPickUpDropOffProps, CarPickUpDropOffState> {
  private dropOffRef = null;
  private invalidDropOffErrorMessage: string;
  private pickUpTimeLabel: string;
  private dropOffTimeLabel: string;
  private pickUpDropOffToggleText: string;
  private dateUtils: DateUtils;

  constructor(props: CarPickUpDropOffProps) {
    super(props);

    const { l10n, registerValidator } = props;

    this.timeRangeValidator = this.timeRangeValidator.bind(this);
    this.focusDropOff = this.focusDropOff.bind(this);

    this.invalidDropOffErrorMessage = l10n.formatText('multiItem.searchWizard.dropOffErrorMsg');
    this.pickUpTimeLabel = l10n.formatText('multiItem.searchWizard.pickUpTime');
    this.dropOffTimeLabel = l10n.formatText('multiItem.searchWizard.dropOffTime');
    this.pickUpDropOffToggleText = l10n.formatText('multiItem.searchWizard.pickUpDropOffToggle');

    this.dateUtils = new DateUtils();

    registerValidator(this.timeRangeValidator);

    this.dropOffRef = React.createRef();
    this.state = {
      invalidText: '',
    };
  }

  public componentDidMount() {
    this.setInvalidText();
  }

  public componentDidUpdate() {
    this.setInvalidText();
  }

  private setInvalidText() {
    const isCurrentTimeRangeValid = !this.timeRangeInvalid;
    const isLastTimeRangeValid = !this.state.invalidText;

    if (isCurrentTimeRangeValid && !isLastTimeRangeValid) {
      this.setState({ invalidText: '' });
    }

    if (!isCurrentTimeRangeValid && isLastTimeRangeValid) {
      this.setState({ invalidText: this.invalidDropOffErrorMessage });
    }
  }

  private focusDropOff() {
    this.dropOffRef.current.focus();
  }

  private timeRangeValidator() {
    const text = this.timeRangeInvalid ? this.invalidDropOffErrorMessage : '';

    this.setState({ invalidText: text });

    if (text) {
      return {
        text,
        focus: this.focusDropOff,
      };
    }

    return '';
  }

  private getTimeInMinutes(timeField) {
    const { hour, minute } = getBexApiDateTimeFromLocKey(timeField);
    const minutesInHour = 60;

    return hour * minutesInHour + minute;
  }

  private get dropOffTimeInMinutes() {
    const daysBetween = this.dateUtils.getDaysBetween(this.props.model.startDate, this.props.model.endDate);
    const minutesInDay = 24 * 60;

    return daysBetween * minutesInDay + this.getTimeInMinutes(this.props.model.dropOffTime);
  }

  private get pickUpTimeInMinutes() {
    return this.getTimeInMinutes(this.props.model.pickUpTime);
  }

  private get timeRangeInvalid(): boolean {
    const { model } = this.props;
    if (!model.startDate || !model.endDate) {
      return false;
    }

    if (!model.pickUpTime || !model.dropOffTime) {
      return true;
    }

    return this.dropOffTimeInMinutes - this.pickUpTimeInMinutes < 120;
  }

  private getFormattedDate(rawDate: string) {
    try {
      return this.props.l10n.formatDateString(rawDate, { skeleton: 'MMMd' }) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  public render() {
    const { l10n, updateModel: onChange, updateModelCallback, model, validators, modelKey } = this.props;

    const { pickUpTime, dropOffTime, startDate: pickUpDate, endDate: dropOffDate } = model;

    const commonProps = {
      updateModelCallback,
      model,
      modelKey,
      validators,
      onChange,
      l10n,
    };

    const timeSelect = (
      <UitkLayoutGrid columns={2} space="two">
        <UitkLayoutGridItem>
          <CarPickUpDropOffTimeSelect
            label={this.pickUpTimeLabel}
            id="pickUpTime"
            value={pickUpTime}
            formattedDate={this.getFormattedDate(pickUpDate)}
            {...commonProps}
          />
        </UitkLayoutGridItem>

        <UitkLayoutGridItem>
          <CarPickUpDropOffTimeSelect
            label={this.dropOffTimeLabel}
            id="dropOffTime"
            invalid={this.state.invalidText}
            value={dropOffTime}
            formattedDate={this.getFormattedDate(dropOffDate)}
            domRef={this.dropOffRef}
            {...commonProps}
          />
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );

    return (
      <Viewport>
        <ViewSmall>
          <>
            <hr className="all-y-margin-three" />
            <UitkHeading tag="h4" size={6}>
              <LocalizedText message="multiItem.searchWizard.mobile.carLabel" />
            </UitkHeading>
            {timeSelect}
          </>
        </ViewSmall>
        <ViewMedium>
          <UitkExpandoLink
            type={UitkExpandoLinkTypeSize.TYPE_300}
            expandoTitle={this.pickUpDropOffToggleText}
            isVisible={false}
            data-stid="expand-multiitem-search-wizard-car-fields"
          >
            {timeSelect}
          </UitkExpandoLink>
        </ViewMedium>
      </Viewport>
    );
  }
}
